import React, {useEffect, useState} from 'react';
import { Grid, Typography, Card, CardContent, CardActionArea, Box, makeStyles } from '@material-ui/core';
import { InsertDriveFile, Storage } from '@material-ui/icons';
import { basicService, vaultsService } from '@app/services';
import { Administrator, ManageVault, VaultExporter } from '@app/store/constants';
import {hasOrgPermission, hasPermission} from '@app/helpers';
import { useTypedTranslation } from '@app/hooks';
import { IModalData, ModalComponent } from '@app/components';
import { getModalSwitch } from '@app/store/actions';
import { useDispatch, useSelector } from 'react-redux';
import { ChooseVaultsForExportDialog } from '@app/pages/password/dialogs/chooseVaultsForExport.dialog';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import ConfirmationPopupBodyComponent from '@app/pages/password/components/confirmationPopupBody';

const useStyles = makeStyles({
    card: {
        minWidth: 300,
        minHeight: 275
    }
});

const PasswordImportChoicePage = () => {
    const { translation } = useTypedTranslation();
    const dispatch = useDispatch();
    const modalSwitchState: boolean = useSelector((state: any) => state.modal);
    const [showConfirm, setShowConfirm] = useState<boolean>(false);
    const [gotIndividual, setGotIndividual] = useState<boolean>(false);
    const [modalData, setModalData] = useState<IModalData>({
        headerTitle: '',
        modalSwitcher: modalSwitchState
    });
    const classes = useStyles();

    const closeConfirm = () => {
        setShowConfirm(false);
    };

    const showConfirmDialog = () => {
        setShowConfirm(true);
    };

    const onExport = () => {
        if (hasPermission([Administrator]) || hasPermission([VaultExporter])) {
            addVaultsForExportModal();
        } else {
            if (gotIndividual) {
                showConfirmDialog();
            } else {
                dispatch(handleSetSnackbar({ show: true, message: translation('EXPORT_VAULTS_NO_INDIVIDUAL'), severity: 'error' }));
            }
        }
    };

    const handleExportVaults = () => {
        vaultsService
            .exportVaults([])
            .then(() => {
                dispatch(handleSetSnackbar({ show: true, message: translation('EXPORT_VAULTS_SUCCESS'), severity: 'success' }));
                closeConfirm();

            })
            .catch(() => {
                dispatch(handleSetSnackbar({ show: true, message: translation('EXPORT_VAULTS_ERROR'), severity: 'error' }));
                closeConfirm();
            });
    };

    const addVaultsForExportModal = () => {
        dispatch(getModalSwitch(true));
        setModalData({
            headerTitle: translation('EXPORT_PASSWORDS'),
            modalSwitcher: modalSwitchState,
            form: <ChooseVaultsForExportDialog />
        });
    };

    useEffect(() => {
        vaultsService.checkIndividual().then((response) => {
            setGotIndividual(response.data);
        }).catch(() => {
            setGotIndividual(false);
        });
    }, []);

    return (
        <>
            <ModalComponent {...modalData} />

            <Grid container direction='column' justifyContent='center'>
                <Grid item xs={12}>
                    <Box m={6}>
                        <Typography align='center' variant='h3'>
                            {translation('PASSWORD_IMPORT_CHOICE_HEADER')}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item>
                    <Grid container direction='row' spacing={3} justifyContent='center' style={{ margin: 0, width: '100%' }}>
                        <Grid item xs={4}>
                            <Card className={classes.card} variant='outlined'>
                                <CardActionArea className={classes.card} onClick={onExport}>
                                    <CardContent>
                                        <Grid container direction='column' spacing={2} justifyContent='space-between' alignItems='center'>
                                            <Grid item>
                                                <Typography align='center' variant='h1'>
                                                    {translation('EXPORT_PASSWORDS')}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <InsertDriveFile
                                                    color='primary'
                                                    style={{ display: 'block', margin: 'auto', height: 140, width: 140 }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                        <Grid item xs={4}>
                            <Card className={classes.card} variant='outlined'>
                                <CardActionArea
                                    className={classes.card}
                                    onClick={() => {
                                        basicService.redirect('/passwordserver/import/password-import');
                                    }}
                                >
                                    <CardContent>
                                        <Grid container direction='column' spacing={2} justifyContent='space-between' alignItems='center'>
                                            <Grid item>
                                                <Typography align='center' variant='h1'>
                                                    {translation('IMPORT_PASSWORDS')}
                                                </Typography>
                                            </Grid>
                                            <Grid item>
                                                <InsertDriveFile
                                                    color='primary'
                                                    style={{ display: 'block', margin: 'auto', height: 140, width: 140 }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                        {hasOrgPermission([Administrator, ManageVault]) && (
                            <Grid item xs={4}>
                                <Card className={classes.card} variant='outlined'>
                                    <CardActionArea
                                        className={classes.card}
                                        onClick={() => {
                                            basicService.redirect('/passwordserver/import/onprem-migrator');
                                        }}
                                    >
                                        <CardContent>
                                            <Grid container direction='column' spacing={2} justifyContent='space-between' alignItems='center'>
                                                <Grid item>
                                                    <Typography align='center' variant='h1'>
                                                        {translation('MIGRATE_ON_PREM_SERVER')}
                                                    </Typography>
                                                </Grid>
                                                <Grid item>
                                                    <Storage color='primary' style={{ display: 'block', margin: 'auto', height: 140, width: 140 }} />
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Grid>
            <ModalComponent
                headerTitle={translation('EXPORT_VAULTS_CONFIRMATION_TITLE')}
                localModalSwitcher={showConfirm}
                modalSwitcherCallback={closeConfirm}
                form={<ConfirmationPopupBodyComponent onCancelClick={closeConfirm} confirmCallback={handleExportVaults} />}
            />
        </>
    );
};

export default PasswordImportChoicePage;
